/**
 * TransactionListContainer
 * 
 * Reusable container component that handles loading/error states
 * and renders TransactionList. Reduces duplication between
 * embedded and non-embedded transaction views.
 */

import React from 'react';
import { Transaction } from '@/services/platformApi';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';
import TransactionList from './TransactionList';

interface TransactionListContainerProps {
  transactions: Transaction[];
  pendingTransactions?: Transaction[];
  completedTransactions?: Transaction[];
  balanceOperations?: Transaction[]; // balance_up and balance_down transactions
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onTransactionSelect: (transaction: Transaction) => void;
  selectedTransaction?: Transaction | null;
  onCloseDetails: () => void;
  // Pagination
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showPagination?: boolean;
  // Display options
  emptyMessage?: string;
  onSeeAll?: () => void;
  showSeparateSections?: boolean;
  sortMode?: 'chronological' | 'grouped';
  // Styling
  loadingPadding?: 'p-4' | 'p-8';
}

const TransactionListContainer: React.FC<TransactionListContainerProps> = ({
  transactions,
  pendingTransactions,
  completedTransactions,
  balanceOperations = [],
  loading,
  error,
  onRetry,
  onTransactionSelect,
  selectedTransaction,
  onCloseDetails,
  currentPage,
  totalPages,
  onPageChange,
  showPagination = true,
  emptyMessage,
  onSeeAll,
  showSeparateSections = false,
  sortMode = 'chronological',
  loadingPadding = 'p-4'
}) => {
  if (loading) {
    return (
      <div className={loadingPadding}>
        <LoadingSpinner text="Loading transactions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={loadingPadding}>
        <ErrorMessage
          message={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <TransactionList
      transactions={transactions}
      onTransactionSelect={onTransactionSelect}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      loading={loading}
      selectedTransaction={selectedTransaction}
      onCloseDetails={onCloseDetails}
      showPagination={showPagination}
      emptyMessage={emptyMessage}
      onSeeAll={onSeeAll}
      pendingTransactions={pendingTransactions}
      completedTransactions={completedTransactions}
      balanceOperations={balanceOperations}
      showSeparateSections={showSeparateSections}
      sortMode={sortMode}
    />
  );
};

export default TransactionListContainer;

